"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { bluetoothHelper } from "@/utils/bluetoothHelper";
import "@fontsource/share-tech-mono";
import { PracticeRepository } from "@/infrastructure/repositories/PracticeRepository";
import { Practice } from "@/domain/entities/Practice";

import {
  ChevronLeft,
  Pause,
  Play,
  RotateCcw,
  Maximize2,
  Minimize2,
  Award,
  Timer,
  Repeat,
} from "lucide-react";

import { ChevronUp, ChevronDown } from "lucide-react";

interface PracticeClockContentProps {
  sets?: any[];
  practiceId?: number | string;
  name?: string;
  onSectionChange?: (section: string, payload?: any) => void;
}

export function PracticeClockContent({
  sets = [],
  practiceId: _practiceId,
  name,
  onSectionChange,
}: PracticeClockContentProps) {
  const totalRounds = sets.length;
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isConnected, setIsConnected] = useState(bluetoothHelper.isConnected());
  const [bluetoothAvailable, setBluetoothAvailable] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [currentRep, setCurrentRep] = useState(1);
  const [report, setReport] = useState<any[]>([]);
  const [isReportVisible, setIsReportVisible] = useState(false);
  const [isClockRealTime, setIsClockRealTime] = useState(false);
  const [realTimeLabel, setRealTimeLabel] = useState("Real Time");
  const [manualChange, setManualChange] = useState(false);
  const isFinishingRef = useRef(false);

  const clockRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const NEON = "#39FF14";
const lastLapTimeRef = useRef<number | null>(null);
  useEffect(() => {
    if (!("bluetooth" in navigator)) setBluetoothAvailable(false);
  }, []);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTimer((t) => +(t + 0.01).toFixed(2));
      }, 10);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning]);

  useEffect(() => {
  if (!isRunning || sets.length === 0) return;

  const currentSet = sets[currentSetIndex];
  if (!currentSet) return;

  const totalSeconds =
    currentSet.minutes * 60 + currentSet.seconds;

  if (!manualChange && timer >= totalSeconds) {
    const lap = {
      setNumber: currentSet.number,
      repNumber: currentRep,
      duration: totalSeconds, // exact target time
      distance: currentSet.distance,
      energy_zone: currentSet.energy_zone,
    };

    setReport(prev => [...prev, lap]);

    // reset reference for next lap
    lastLapTimeRef.current = performance.now();

    if (currentRep < currentSet.reps) {
      setCurrentRep(r => r + 1);
      setTimer(0);
    } else if (currentSetIndex < sets.length - 1) {
      setCurrentSetIndex(i => i + 1);
      setCurrentRep(1);
      setTimer(0);
      toast.info(`Next Set: ${sets[currentSetIndex + 1].energy_zone}`);
    } else {
      handleFinish();
    }
  }

  setManualChange(false);
}, [timer, isRunning]);



  const trySendBluetooth = async (message: string) => {
    if (!bluetoothAvailable || !isConnected) return;
    try {
      await bluetoothHelper.sendString(message);
    } catch {
      console.warn("Bluetooth command failed:", message);
    }
  };


const handleStart = async () => {
  try {
    setIsStarting(true);
    isFinishingRef.current = false;

    setTimer(0);
    setCurrentSetIndex(0);
    setCurrentRep(1);
    setReport([]);
    setIsReportVisible(false);

    // Initialize lap timing reference
    lastLapTimeRef.current = performance.now();

    if (bluetoothAvailable && !isConnected) {
      try {
        const result = await bluetoothHelper.connect();
        setIsConnected(true);
        toast.success(`Connected to ${result.name}`);
        await new Promise((r) => setTimeout(r, 500));
      } catch {
        console.warn("Bluetooth connection failed — running locally");
      }
    }

    await trySendBluetooth("RESET");
    await new Promise((r) => setTimeout(r, 600));
    await trySendBluetooth("START");

    setIsRunning(true);
    setIsPaused(false);
    toast.success("Practice started");
  } catch {
    toast.error("Failed to start");
  } finally {
    setIsStarting(false);
  }
};


  const handlePause = async () => {
    await trySendBluetooth("PAUSE");
    setIsRunning(false);
    setIsPaused(true);
  };

  const handleResume = async () => {
    await trySendBluetooth("RESUME");
    setIsRunning(true);
    setIsPaused(false);
  };

  const handleReset = async () => {
  await trySendBluetooth("RESET");
  isFinishingRef.current = false;

  setTimer(0);
  setIsRunning(false);
  setIsPaused(false);
  setCurrentSetIndex(0);
  setCurrentRep(1);
  setReport([]);
  setIsReportVisible(false);

  // Clear lap timing reference
  lastLapTimeRef.current = null;
};

const findPracticeById = (
  practices: Practice[],
  practiceId: number | string
): Practice | null => {
  const targetId = String(practiceId);
  return (
    practices.find(
      p =>
        String(p.practiceId) === targetId ||
        String(p.id) === targetId
    ) ?? null
  );
};

const mapReportToSaveLaps = (
  report: any[],
  practiceLogicalId: string,
  swimmerId: number,
  coachId: number
) => {
  const nowISO = new Date().toISOString();
  return report.map((lap, index) => ({
    id: 0,
    practice_lap_id: 0,
    practice_id: practiceLogicalId,
    rep: String(index + 1),
    distance: String(lap.distance ?? 0),
    interval: String(Math.max(0, Math.round(Number(lap.duration) || 0))),
    energy_zone: lap.energy_zone ?? "",
    description: `Set ${lap.setNumber ?? 1} Rep ${lap.repNumber ?? index + 1}`,
    time: 0,
    createdAt: nowISO,
    updated_at: nowISO,
    isNoEditable: false,
    isPassed: 0,
    user_id: String(swimmerId),
    coach_id: String(coachId),
    passedRepNumber: Number(lap.repNumber ?? index + 1),
  }));
};

const mapExistingLapsToSaveLaps = (
  laps: any[],
  practiceLogicalId: string,
  swimmerId: number,
  coachId: number
) => {
  const nowISO = new Date().toISOString();
  return laps.map((lap: any) => ({
    id: 0,
    practice_lap_id: 0,
    practice_id: practiceLogicalId,
    rep: String(lap.rep ?? 1),
    distance: String(lap.distance ?? 0),
    interval: String(lap.interval ?? lap.interval_lap ?? 0),
    energy_zone: lap.energy_zone ?? "",
    description: lap.description ?? "",
    time: Number(lap.time ?? 0),
    createdAt: lap.createdAt ?? nowISO,
    updated_at: nowISO,
    isNoEditable: lap.isNoEditable === true || lap.isnoEditable === 1 || lap.isnoEditable === "1",
    isPassed: Number(lap.isPassed ?? 0),
    user_id: String(lap.user_id ?? swimmerId),
    coach_id: String(lap.coach_id ?? coachId),
    passedRepNumber: Number(lap.passedRepNumber ?? lap.passedRapNumber ?? 0),
  }));
};



const practiceRepo = new PracticeRepository();

const parseStablePracticeDate = (value: unknown): Date => {
  if (!value) return new Date();
  const raw = String(value).trim();
  if (!raw) return new Date();

  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]);
    const d = Number(ymd[3]);
    return new Date(y, m - 1, d, 12, 0, 0);
  }

  const mdy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const m = Number(mdy[1]);
    const d = Number(mdy[2]);
    const y = Number(mdy[3]);
    return new Date(y, m - 1, d, 12, 0, 0);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const finishPracticePersist = async (reportOverride?: any[]) => {
  if (!_practiceId) return;
  const reportData = reportOverride ?? report;

  try {
    const swimmerId = Number(
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id") ||
      0
    );
    const coachId = Number(
      localStorage.getItem("coach_id") ||
      localStorage.getItem("current_user_coach_id") ||
      0
    );

    if (!swimmerId) {
      throw new Error("Swimmer not found for finishing practice");
    }

    const { practices, laps } =
      await practiceRepo.getAllPractices(String(swimmerId));

    const practice = findPracticeById(practices, _practiceId);
    if (!practice) throw new Error("Practice not found");

    const selectedPractice = JSON.parse(
      localStorage.getItem("selected_practice") || "{}"
    );
    const selectedPracticeIds = new Set(
      [
        selectedPractice?.id,
        selectedPractice?.practice_id,
        selectedPractice?.practiceId,
        selectedPractice?.folder_practice_model_id,
      ]
        .filter(Boolean)
        .map((v) => String(v))
    );
    const isSelectedPracticeMatch =
      selectedPracticeIds.has(String(_practiceId)) ||
      selectedPracticeIds.has(String(practice.id)) ||
      selectedPracticeIds.has(String(practice.practiceId));

    // 1. Delete ALL rows for this logical practice (string-safe matching)
    const targetLogicalId = String(practice.practiceId || practice.id || _practiceId);
    const rowsToDelete = practices.filter((p) => {
      const ids = [p.practiceId, p.id].map((x) => String(x));
      return ids.includes(targetLogicalId);
    });

    // Fallback: if list mapping misses the row, at least remove current practice id.
    const deleteIds = Array.from(
      new Set(
        [
          String(_practiceId),
          targetLogicalId,
          ...rowsToDelete.map((p) => String(p.id)),
          ...rowsToDelete.map((p) => String(p.practiceId)),
        ].filter(Boolean)
      )
    );

    for (const deleteId of deleteIds) {
      await practiceRepo.delete(deleteId, swimmerId);
    }

    // 2. Recreate with SAME logical id
    const practiceJson = practice.toJSON();
    const selectedPracticeDateRaw = isSelectedPracticeMatch
      ? selectedPractice?.practice_date ??
        selectedPractice?.PracticeDate ??
        selectedPractice?.date
      : null;
    const preservedDate = parseStablePracticeDate(
      selectedPracticeDateRaw ?? practiceJson.practice_date ?? (practice as any).date
    );
    const preservedPracticeTime =
      (isSelectedPracticeMatch
        ? selectedPractice?.practice_time ?? selectedPractice?.practiceTime
        : null) ||
      practice.practice_time ||
      "AM";
    const preservedCourseTypeLookupId =
      Number(
        (isSelectedPracticeMatch
          ? selectedPractice?.courseTypeLookupId ?? selectedPractice?.CourseTypeLookupId
          : null) ??
        practice.courseTypeLookupId ??
          (practiceJson as any).practice_CourseTypeLookupId ??
          1
      ) || 1;

    const finishedPractice = Practice.create({
      id: targetLogicalId,
      practiceId: targetLogicalId,
      coachId: practice.coachId || coachId,
      swimmerId: practice.swimmerId || swimmerId,
      name: practice.name,
      description: practice.description || "",
      date: preservedDate,
      practice_time: preservedPracticeTime,
      courseTypeLookupId: preservedCourseTypeLookupId,
      isFavorite: Boolean(practice.isFavorite),
      isFinished: true,
      imageData: practice.imageData || null,
      folderId: practice.folderId || null,
      createdAt: practice.createdAt,
      updatedAt: new Date(),
    });

    // 3. Map laps to logical id
    const existingLaps = Array.isArray(laps)
      ? laps.filter(
          (lap: any) =>
            String(
              lap.practice_id ??
              lap.practiceId
            ) === targetLogicalId
        )
      : [];

    const mappedLaps =
      reportData.length > 0
        ? mapReportToSaveLaps(
            reportData,
            targetLogicalId,
            swimmerId,
            coachId
          )
        : mapExistingLapsToSaveLaps(
            existingLaps,
            targetLogicalId,
            swimmerId,
            coachId
          );

    // 4. Save
    await practiceRepo.save(
      coachId,
      swimmerId,
      finishedPractice,
      mappedLaps,
      ""
    );

    toast.success("Practice finished successfully");
  } catch (err) {
    console.error(err);
    toast.error("Failed to finish practice");
  } finally {
    isFinishingRef.current = false;
  }
};





const handleFinish = async () => {
  // 🔒 Prevent double execution
  if (isFinishingRef.current) return;
  isFinishingRef.current = true;

  try {
    await trySendBluetooth("STOP");

    setIsRunning(false);
    setIsPaused(false);
    const now = performance.now();
    const currentSet = sets[currentSetIndex];
    let nextReport = report;

    if (currentSet) {
      const elapsedSeconds =
        lastLapTimeRef.current !== null
          ? (now - lastLapTimeRef.current) / 1000
          : timer;

      const lap = {
        setNumber: currentSet.number,
        repNumber: currentRep,
        duration: elapsedSeconds,
        distance: currentSet.distance,
        energy_zone: currentSet.energy_zone,
      };

      const last = report[report.length - 1];
      const isDuplicate =
        last &&
        last.setNumber === lap.setNumber &&
        last.repNumber === lap.repNumber &&
        Math.abs(last.duration - lap.duration) < 0.01;

      if (!isDuplicate && elapsedSeconds > 0.05) {
        nextReport = [...report, lap];
        setReport(nextReport);
      }
    }

    lastLapTimeRef.current = null;

    await finishPracticePersist(nextReport);

    toast.success("Practice finished!");
    setIsReportVisible(true);
  } catch (err) {
    console.error(err);
    toast.error("Failed to finish practice");
  }
};



  const formatTime = (seconds: number) => {
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60).toString().padStart(2, "0");
    const secs = (total % 60).toString().padStart(2, "0");
    const ms = ((seconds % 1) * 100).toFixed(0).padStart(2, "0");
    return `${mins}:${secs}.${ms}`;
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await clockRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error("Fullscreen toggle failed:", err);
    }
  };

  const handleToggleRealTime = async () => {
    try {
      const newValue = !isClockRealTime;
      setIsClockRealTime(newValue);

      const label = newValue ? "Pace" : "RealTime";
      setRealTimeLabel(label);

      await trySendBluetooth(newValue ? "wall" : "pace");
    } catch (err) {
      console.error("RealTime toggle failed:", err);
    }
  };

const handleRepUp = () => {
  if (!currentSet) return;

  setManualChange(true);
  setTimer(0);
  
  if (currentRep > 1) {
    setCurrentRep(r => r - 1);
    return;
  }

  if (currentSetIndex > 0) {
    const prevSetIndex = currentSetIndex - 1;
    const prevSet = sets[prevSetIndex];

    setCurrentSetIndex(prevSetIndex);
    setCurrentRep(prevSet.reps);
    return;
  }

};


const handleRepDown = () => {
  if (!currentSet) return;

  // If auto-lap already fired, do nothing
  if (timer === 0) return;

  setManualChange(true);

  const isLastRep = currentRep === currentSet.reps;
  const isLastSet = currentSetIndex === sets.length - 1;

  if (isLastRep && isLastSet) return;

  const now = performance.now();

  const duration =
    lastLapTimeRef.current !== null
      ? (now - lastLapTimeRef.current) / 1000
      : timer;

  lastLapTimeRef.current = now;

  const lap = {
    setNumber: currentSet.number,
    repNumber: currentRep,
    duration,
    distance: currentSet.distance,
    energy_zone: currentSet.energy_zone,
  };

  setReport((prev) => [...prev, lap]);

  if (!isLastRep) {
    setCurrentRep((r) => r + 1);
    setTimer(0);
  } else {
    setCurrentSetIndex((i) => i + 1);
    setCurrentRep(1);
    setTimer(0);
    toast.info(`Next Set: ${sets[currentSetIndex + 1].energy_zone}`);
  }
};

const [fontsReady, setFontsReady] = useState(false);

useEffect(() => {
  document.fonts.ready.then(() => {
    setFontsReady(true);
  });
}, []);

  const currentSet = sets[currentSetIndex];

  if (isReportVisible) {
    const totalTime = report.reduce((sum, r) => sum + r.duration, 0);
    const grouped = sets.map((s) => {
      const laps = report.filter((r) => r.setNumber === s.number);
      const totalSetTime = laps.reduce((sum, l) => sum + l.duration, 0);
      const avg = laps.length ? totalSetTime / laps.length : 0;
      return { ...s, totalSetTime, avg };
    });
    

    return (
      <div  className="-m-6 min-h-screen bg-gradient-to-b from-black via-gray-900 to-gray-950 text-white flex flex-col items-center py-10 px-4">
        <h1 className="text-4xl font-bold mb-3 flex items-center gap-2">
          <Award className="text-yellow-400" /> Practice Summary Report
        </h1>
        <p className="text-gray-400 mb-10 text-center text-lg">
          {name} • {sets.length} Sets • Total Time:{" "}
          <span className="text-green-400">{formatTime(totalTime)}</span>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 w-full max-w-6xl">
          {grouped.map((s, idx) => (
            <div
              key={idx}
              className="bg-gray-800/70 border border-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-green-500/20 transition"
            >
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-2xl font-semibold text-green-400">
                  Set {s.number}
                </h2>
                <span className="px-3 py-1 text-sm rounded-full bg-green-600/30 border border-green-500/40">
                  {s.energy_zone}
                </span>
              </div>

              <p className="text-gray-300 mb-1">
                <strong>Distance:</strong> {s.distance}m × {s.reps} reps
              </p>
              <p className="text-gray-300 mb-1">
                <strong>Total Time:</strong> {formatTime(s.avg)}
              </p>

              <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <Timer size={16} />
                  <span>{s.minutes}m {s.seconds}s target</span>
                </div>
                <div className="flex items-center gap-1">
                  <Repeat size={16} />
                  <span>{s.reps} reps</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary footer */}
        <div className="mt-10 text-center">
          <p className="text-xl text-gray-300 mb-2">
            🏊 Great work! You completed {sets.length} sets.
          </p>
          <p className="text-gray-400">
            Total Duration:{" "}
            <span className="text-green-400 font-semibold">
              {formatTime(totalTime)}
            </span>
          </p>
        </div>

        {/* Action buttons */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button
            onClick={() => {
              setIsReportVisible(false);
              setTimer(0);
              setIsRunning(false);
              setIsPaused(false);
              setCurrentSetIndex(0);
              setCurrentRep(1);
              isFinishingRef.current = false;
              lastLapTimeRef.current = null;
            }}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg"
          >
            Back to Clock
          </Button>

          <Button
            onClick={() => handleStart()}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg"
          >
            Restart Practice
          </Button>

          <Button
            onClick={() => onSectionChange?.("sets")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
          >
            Return to Sets
          </Button>
        </div>
      </div>
    );
  }

return (
  <div style={{
      opacity: fontsReady ? 1 : 0,
      transition: "opacity 0.1s ease",
    }}
    ref={clockRef}
    className="flex flex-col min-h-screen bg-background text-white -m-6 sm:-m-6 overflow-hidden"
  >
    {/* 1. HEADER */}
    <header className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-black/70">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() => onSectionChange?.("sets")}
          className="flex items-center text-lg font-medium"
        >
          <ChevronLeft className="mr-1 w-5 h-5" />
          Back
        </Button>

          <Button
            onClick={handleStart}
            disabled={isStarting}
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-6 py-2 rounded-md"
          >
            Start
          </Button>
        </div>

      <div className="text-center flex-1">
        <h1 className="text-2xl font-semibold">Practice Clock</h1>
      </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleFinish}
            disabled={isFinishingRef.current}
            className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-6 py-2 rounded-md"
          >
            Finish
          </Button>

          <Button
            onClick={handleToggleRealTime}
            className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-4 py-2 rounded-md flex items-center gap-2"
          >
            {realTimeLabel}
          </Button>

          <Button
            onClick={toggleFullscreen}
            className="text-lg px-4 py-2 rounded-md flex items-center gap-2"
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5" />
            ) : (
              <Maximize2 className="w-5 h-5" />
            )}
          </Button>

          
        </div>
      </header>

    <main className="flex flex-col flex-1 bg-black relative overflow-hidden pt-10">

      {/* 2. ROUND + REPS SECTION */}
      <section className="flex items-center justify-between px-10 mt-4">
        {/* ROUND */}
        <div className="text-center">
          <div className="flex items-center justify-between text-gray-400 text-4xl tracking-widest drop-shadow-lg">
            <span>ROUND</span>
            <span>({totalRounds})</span>
          </div>
          <div
            style={{
              ...digitalStyle,
              color: NEON,
            }}
          >
            {currentSetIndex + 1}
          </div>
        </div>

        {/* REPS */}
        <div className="text-center">
          <div className="flex items-center justify-between text-gray-400 text-4xl tracking-widest drop-shadow-lg">
            <span>REPS</span>
            <span>({currentSet?.reps ?? 0})</span>
          </div>
          <div
            style={{
              ...digitalStyle,
              color: NEON,
             
            }}
          >
            {currentRep}
          </div>
        </div>
      </section>

      {/* 3. TIMER SECTION */}
    <section className="flex items-center justify-center mt-13 gap-24">
  
  {/* TIMER */}
  <span
    style={{
      ...digitalStyle,
      fontSize: "clamp(4rem, 18vw, 20rem)",
    }}
  >
    {formatTime(timer)}
  </span>

  {/* UP / DOWN BUTTONS */}
  <div className="flex flex-col gap-4">
    <Button
      variant="outline"
      className="p-3 rounded-full bg-gray-700 hover:bg-gray-600"
      onClick={handleRepUp}
    >
      <ChevronUp className="w-8 h-8" />
    </Button>

    <Button
      variant="outline"
      className="p-3 rounded-full bg-gray-700 hover:bg-gray-600"
      onClick={handleRepDown}
    >
      <ChevronDown className="w-8 h-8" />
    </Button>
  </div>

</section>


      {/* 4. SET DETAILS SECTION */}
      {currentSet && (
        <section className="text-center px-6 mt-14">
          <p className="text-4xl sm:text-5xl font-semibold text-gray-200 drop-shadow-2xl leading-snug">
            {currentSet.energy_zone} • {currentSet.distance}m × {currentSet.reps} reps ({currentSet.minutes}m {currentSet.seconds}s)
          </p>
        </section>
      )}

      {/* 5. BUTTON SECTION */}
      <section className="flex flex-wrap justify-center gap-8 mt-24 mb-16">
          
        {isRunning && (
          <Button
            onClick={handlePause}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-10 py-6 text-2xl rounded-3xl shadow-xl"
          >
            <Pause className="mr-3 h-7 w-7" />
            Pause
          </Button>
        )}

        {!isRunning && isPaused && (
          <Button
            onClick={handleResume}
            className="bg-green-600 hover:bg-green-700 text-white px-10 py-6 text-2xl rounded-3xl shadow-xl"
          >
            <Play className="mr-3 h-7 w-7" />
            Resume
          </Button>
        )}

        <Button
          onClick={handleReset}
          className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-2xl rounded-3xl shadow-xl"
        >
          <RotateCcw className="mr-3 h-7 w-7" />
          Reset
        </Button>
        
      </section>

    </main>
  </div>
);
}


const digitalStyle = {
  fontFamily: "'Digital-7 Mono', monospace",
  color: "#39FF14",
  fontSize: "clamp(3rem, 12vw, 14rem)",
  lineHeight: 1,
  textShadow: "0 0 25px #39FF14",
};

